import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Tier, TIER_TO_TRACK_CODE, EducationalInvestment, AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { WhatIsACareerPathModal } from '@backrs_ui/grow-ui/src/pages/CareerPath/molecules/careerPathPage/WhatIsACareerPathModal';
import { hydrateEducationalInvestment } from '@backrs_ui/grow-ui/src/pages/EducationalInvestment/utils';
import { useGetEducationalInvestmentsByCareerTrackQuery } from '@backrs_ui/grow-ui/src/redux/educationalInvestment/educationalInvestment.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import { filterCareerPathSuggestionsByScoreSection } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathSuggestions';
import { OwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

import { Box, Button, Chip, Collapse, IconButton, Typography, SxProps, Theme } from '@mui/material';

import { CareerPathPill } from '../CareerPathPill';
import { CareerTracksModal } from '../CareerTracksModal';
import { EducationalInvestmentsModal } from '../EducationalInvestmentsModal';
import { MinimumTrackCard } from '../MinimumTrackCard';
import { OwnershipCard } from '../OwnershipCard';
import { OwnershipLevelsModal } from '../OwnershipLevelsModal';

const STRINGS = appStrings.modules.survey.output.blueprintSection.exploreTradeoffsView;

export type RequirementSectionKey = 'careerTrack' | 'education' | 'careerPaths' | 'ownership';

const TOP_PATHS_DEFAULT_ROWS = 3;
// Sub-pixel layouts can put pills in the same visual row at slightly different
// `top` values. A few pixels of tolerance keeps row grouping stable.
const ROW_TOLERANCE_PX = 4;

type TradeoffResultsPanelProps = Readonly<{
  draftTier: Tier;
  draftCareerPathSuggestions: CareerPathWithScore[];
  ownershipLevel: OwnershipLevel | null;
  registerSectionRef?: (key: RequirementSectionKey, el: HTMLElement | null) => void;
}>;

type InfoModalKey = 'careerTrack' | 'education' | 'careerPaths' | 'ownership';

export function TradeoffResultsPanel({
  draftTier,
  draftCareerPathSuggestions,
  ownershipLevel,
  registerSectionRef,
}: TradeoffResultsPanelProps) {
  const [openModal, setOpenModal] = useState<InfoModalKey | null>(null);
  const [showAllPaths, setShowAllPaths] = useState(false);

  const careerPaths = useAppSelector((state: RootState) => state.careerPath.careerPaths);
  const careerCategories = useAppSelector((state: RootState) => state.careerPath.careerCategories);

  const sectionRefCb = useCallback(
    (key: RequirementSectionKey) => (el: HTMLElement | null) => registerSectionRef?.(key, el),
    [registerSectionRef],
  );

  const {
    data: rawEducationalInvestment,
    isError: isEducationError,
    refetch: refetchEducation,
  } = useGetEducationalInvestmentsByCareerTrackQuery({
    careerTrackCode: TIER_TO_TRACK_CODE[draftTier],
  });

  const educationalInvestment = rawEducationalInvestment
    ? {
        target: rawEducationalInvestment.target.map(hydrateEducationalInvestment),
        others: rawEducationalInvestment.others.map(hydrateEducationalInvestment),
      }
    : undefined;

  const topPaths = filterCareerPathSuggestionsByScoreSection(draftCareerPathSuggestions, 'TOP');

  // Pills wrap horizontally; clamp the TOP pills container to the bottom of the
  // 3rd row when collapsed. We always render the full pill list and constrain
  // height via CSS so the measurement stays accurate across resizes.
  const topPillsRef = useRef<HTMLDivElement>(null);
  const [topCollapsedHeight, setTopCollapsedHeight] = useState<number | null>(null);
  const [topOverflows, setTopOverflows] = useState<boolean>(false);

  useLayoutEffect(() => {
    const measure = () => {
      const wrap = topPillsRef.current;

      if (!wrap) return;

      const children = Array.from(wrap.children) as HTMLElement[];

      if (children.length === 0) {
        setTopCollapsedHeight(null);
        setTopOverflows(false);

        return;
      }

      const wrapTop = wrap.getBoundingClientRect().top;
      const rowTops: number[] = [];
      let thirdRowBottom: number | null = null;
      let hasOverflow = false;

      for (const child of children) {
        const rect = child.getBoundingClientRect();
        const lastTop = rowTops[rowTops.length - 1];
        const isNewRow = rowTops.length === 0 || Math.abs(rect.top - lastTop) > ROW_TOLERANCE_PX;

        if (isNewRow) {
          if (rowTops.length === TOP_PATHS_DEFAULT_ROWS) {
            hasOverflow = true;
            break;
          }

          rowTops.push(rect.top);
        }

        if (rowTops.length <= TOP_PATHS_DEFAULT_ROWS) {
          thirdRowBottom = rect.bottom;
        }
      }

      setTopCollapsedHeight(thirdRowBottom !== null ? thirdRowBottom - wrapTop : null);
      setTopOverflows(hasOverflow);
    };

    measure();

    const wrap = topPillsRef.current;

    if (!wrap) return;

    const observer = new ResizeObserver(measure);

    observer.observe(wrap);

    return () => observer.disconnect();
  }, [topPaths.length]);

  return (
    <Box sx={containerSx}>
      {/* Career Track */}
      <SectionCard
        sectionRef={sectionRefCb('careerTrack')}
        label={STRINGS.careerTrackLabel}
        onInfoClick={() => setOpenModal('careerTrack')}
        infoIconId="tradeoff-career-track-info-btn"
      >
        <Box sx={trackPillsWrapSx}>
          <MinimumTrackCard tier={draftTier} interactive={false} selectedOnly />
        </Box>
      </SectionCard>

      {/* Educational Investments */}
      <SectionCard
        sectionRef={sectionRefCb('education')}
        label={STRINGS.educationLabel}
        onInfoClick={() => setOpenModal('education')}
        infoIconId="tradeoff-education-info-btn"
      >
        {isEducationError ? (
          <Box sx={educationErrorSx}>
            <Typography
              variant="text-sm-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {STRINGS.educationUnavailable}
            </Typography>
            <Button
              id="tradeoff-education-retry-btn"
              hierarchy="Secondary gray"
              size="md"
              onClick={refetchEducation}
            >
              {STRINGS.retry}
            </Button>
          </Box>
        ) : (
          <Box sx={pillsWrapSx}>
            {educationalInvestment?.target.map((inv: EducationalInvestment) => (
              <Chip
                id={`tradeoff-target-investment-${inv.id}`}
                key={inv.id}
                variant="badge"
                size="md"
                type="leading"
                label={inv.name}
                icon={
                  inv.icon ? (
                    <AppIcon iconName={inv.icon} diameter="12px" color="Brand/700" />
                  ) : undefined
                }
                sx={targetInvestmentChipSx}
              />
            ))}
          </Box>
        )}
      </SectionCard>

      {/* Career Paths */}
      <SectionCard
        sectionRef={sectionRefCb('careerPaths')}
        label={STRINGS.careerPathsLabel}
        onInfoClick={() => setOpenModal('careerPaths')}
        infoIconId="tradeoff-career-paths-info-btn"
      >
        <Collapse in={showAllPaths} collapsedSize={topCollapsedHeight ?? 0} timeout={300}>
          <Box ref={topPillsRef} sx={pillsWrapSx}>
            {topPaths.map((path) => (
              <CareerPathPill
                key={path.careerPath.id}
                id={path.careerPath.id}
                name={path.careerPath.name}
                scoreSection={path.scoreSection}
                interactive={false}
                variant="solid"
              />
            ))}
          </Box>
        </Collapse>
        {topOverflows && (
          <Typography
            id="tradeoff-see-all-fits"
            variant="text-sm-semibold"
            onClick={() => setShowAllPaths((prev) => !prev)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowAllPaths((prev) => !prev);
              }
            }}
            role="button"
            tabIndex={0}
            sx={seeAllLinkSx}
          >
            {showAllPaths ? STRINGS.showFewer : STRINGS.seeAllFits}
          </Typography>
        )}
      </SectionCard>

      {/* Ownership */}
      <SectionCard
        sectionRef={sectionRefCb('ownership')}
        label={STRINGS.ownershipLabel}
        onInfoClick={() => setOpenModal('ownership')}
        infoIconId="tradeoff-ownership-info-btn"
      >
        <OwnershipCard ownershipLevel={ownershipLevel} interactive={false} />
      </SectionCard>

      <CareerTracksModal
        open={openModal === 'careerTrack'}
        onClose={() => setOpenModal(null)}
        initialTier={draftTier}
      />
      <EducationalInvestmentsModal
        open={openModal === 'education'}
        onClose={() => setOpenModal(null)}
        target={educationalInvestment?.target ?? []}
        others={educationalInvestment?.others ?? []}
      />
      <WhatIsACareerPathModal
        open={openModal === 'careerPaths'}
        onClose={() => setOpenModal(null)}
        careerPaths={careerPaths}
        careerCategories={careerCategories}
      />
      <OwnershipLevelsModal
        open={openModal === 'ownership'}
        onClose={() => setOpenModal(null)}
        ownershipLevel={ownershipLevel ?? 'important'}
      />
    </Box>
  );
}

type SectionCardProps = Readonly<{
  sectionRef: (el: HTMLElement | null) => void;
  label: string;
  onInfoClick?: () => void;
  infoIconId?: string;
  children: React.ReactNode;
}>;

function SectionCard({ sectionRef, label, onInfoClick, infoIconId, children }: SectionCardProps) {
  return (
    <Box ref={sectionRef} sx={sectionCardSx}>
      <Box sx={sectionHeaderSx}>
        <Typography variant="text-sm-semibold" sx={sectionTitleSx}>
          {label}
        </Typography>
        {onInfoClick && (
          <IconButton
            id={infoIconId}
            onClick={onInfoClick}
            aria-label={STRINGS.infoIconAriaLabel}
            size="small"
            sx={infoButtonSx}
          >
            <AppIcon iconName="InfoCircle" diameter="16px" color="currentColor" />
          </IconButton>
        )}
      </Box>
      <Box sx={sectionContentSx}>{children}</Box>
    </Box>
  );
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const sectionCardSx: SxProps<Theme> = ({ backrs, typography }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Gray/500']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
  overflow: 'hidden',
  // Normalize all pill/chip label text to text-sm-medium for consistency
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
  },
});

const sectionHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-2xl']} ${backrs.spacing['spacing-md']}`,
  color: backrs.colors['Colors/Text/text-secondary'],
});

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const sectionContentSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-2xl']}`,
});

const infoButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: 0,
  color: backrs.colors['Gray/400'],
  '&:hover': {
    color: backrs.colors['Colors/Foreground/fg-tertiary'],
    backgroundColor: 'transparent',
  },
});

const trackPillsWrapSx: SxProps<Theme> = {
  // Override MinimumTrackCard's column layout to wrap horizontally
  '& > *': {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
};

const pillsWrapSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const targetInvestmentChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  cursor: 'default',
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
    ...typography['text-sm-medium'],
  },
  // Override MUI Chip's default hover — these chips are display-only here.
  '&:hover': {
    backgroundColor: backrs.colors['Brand/50'],
  },
});

const seeAllLinkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/600'],
  cursor: 'pointer',
  paddingTop: backrs.spacing['spacing-lg'],
  '&:hover': {
    textDecoration: 'underline',
  },
});

const educationErrorSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  py: backrs.spacing['spacing-sm'],
});
